import store from "@/store";
import router from "@/router";

const AUTH_EXPIRED_KEY = "easyhmo_auth_expired_at";
const AUTH_EXPIRED_NOTICE_KEY = "easyhmo_auth_expired_notice";
const AUTH_EXPIRED_EVENT = "easyhmo-auth-expired";
const AUTH_EXPIRED_MESSAGE = "Your session has expired. Please sign in again.";

let authInvalidated = false;
let authRedirecting = false;
let authGuardInitialized = false;

const hasWindow = () => typeof window !== "undefined";

const getStoredAuthExpiry = () => {
  if (!hasWindow()) {
    return null;
  }

  try {
    return window.localStorage.getItem(AUTH_EXPIRED_KEY);
  } catch {
    return null;
  }
};

const persistAuthExpiry = () => {
  if (!hasWindow()) {
    return;
  }

  try {
    window.localStorage.setItem(AUTH_EXPIRED_KEY, String(Date.now()));
    window.localStorage.setItem(AUTH_EXPIRED_NOTICE_KEY, AUTH_EXPIRED_MESSAGE);
    window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
  } catch {
    // Private browsing/storage failures should not keep the app noisy.
  }
};

const clearStoredAuthExpiry = () => {
  if (!hasWindow()) {
    return;
  }

  try {
    window.localStorage.removeItem(AUTH_EXPIRED_KEY);
    window.localStorage.removeItem(AUTH_EXPIRED_NOTICE_KEY);
  } catch {
    // Nothing to clear when browser storage is unavailable.
  }
};

const clearUserState = () => {
  if (store.state?.user) {
    store.commit("loadUser", null);
    store.commit("loadCluster", null);
    store.commit("loadCache", []);
    store.commit("loadDashboardFreshSignin", false);
  }
};

const redirectToLogin = () => {
  if (authRedirecting || router.currentRoute?.value?.name === "login") {
    return;
  }

  authRedirecting = true;
  router.replace({ name: "login" }).catch(() => {});
};

const markAuthExpired = ({ persist = true, redirect = true } = {}) => {
  authInvalidated = true;

  if (persist) {
    persistAuthExpiry();
  }

  clearUserState();

  if (redirect) {
    redirectToLogin();
  }
};

const resetAuthRequestGuard = () => {
  authInvalidated = false;
  authRedirecting = false;
  clearStoredAuthExpiry();
};

const isPublicAuthEndpoint = (table) => {
  return String(table || "").startsWith("authority/signin");
};

const isAuthExpired = () => {
  if (authInvalidated) {
    return true;
  }

  if (getStoredAuthExpiry()) {
    authInvalidated = true;
    return true;
  }

  return false;
};

const canMakeAuthenticatedRequest = (table = null) => {
  if (isPublicAuthEndpoint(table)) {
    return true;
  }

  return !isAuthExpired();
};

const handleUnauthorizedResponse = (table = null) => {
  if (isPublicAuthEndpoint(table)) {
    return;
  }

  markAuthExpired();
};

const consumeAuthExpiredNotice = () => {
  if (!hasWindow()) {
    return null;
  }

  try {
    const message = window.localStorage.getItem(AUTH_EXPIRED_NOTICE_KEY);
    window.localStorage.removeItem(AUTH_EXPIRED_NOTICE_KEY);
    return message;
  } catch {
    return null;
  }
};

const initializeAuthRequestGuard = () => {
  if (!hasWindow() || authGuardInitialized) {
    return;
  }

  authGuardInitialized = true;

  window.addEventListener("storage", (event) => {
    if (event.key === AUTH_EXPIRED_KEY && event.newValue) {
      markAuthExpired({ persist: false });
    }
  });

  window.addEventListener(AUTH_EXPIRED_EVENT, () => {
    markAuthExpired({ persist: false });
  });

  if (getStoredAuthExpiry()) {
    markAuthExpired({ persist: false });
  }
};

export {
  canMakeAuthenticatedRequest,
  consumeAuthExpiredNotice,
  handleUnauthorizedResponse,
  initializeAuthRequestGuard,
  isAuthExpired,
  resetAuthRequestGuard,
};
